import React from 'react';
import { FileText, Clock, AlertCircle, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DocumentacionPopover } from './DocumentacionPopover';

interface ParteData {
  id: string;
  numero_parte: string | null;
  fecha_trabajo: string;
  descripcion_trabajo: string;
  duracion_total: string | null;
  estado: string;
  firma_cliente: string | null;
  tecnico?: { nombre: string } | null;
}

interface JornadaData {
  id: string;
  fecha: string;
  hora_inicio: string;
  hora_fin: string | null;
  horas_trabajadas: number | null;
  tipo_trabajo: string;
  descripcion_trabajo: string | null;
  tecnico?: { nombre: string } | null;
}

interface DocumentacionIndicatorProps {
  partesCount: number;
  jornadasCount: number;
  estado: string;
  variant?: 'compact' | 'full' | 'badges';
  className?: string;
  ordenId?: string;
  partesData?: ParteData[];
  jornadasData?: JornadaData[];
}

export const DocumentacionIndicator: React.FC<DocumentacionIndicatorProps> = ({
  partesCount,
  jornadasCount,
  estado,
  variant = 'badges',
  className,
  ordenId,
  partesData = [],
  jornadasData = [],
}) => {
  const hasParte = partesCount > 0;
  const hasJornada = jornadasCount > 0;
  const isCompleted = estado === 'finalizada';
  const needsDocumentation = isCompleted && !hasParte && !hasJornada;
  const partialDocumentation = isCompleted && (hasParte !== hasJornada);
  const isClickable = ordenId && (partesData.length > 0 || jornadasData.length > 0);

  const renderParteBadge = () => (
    <span className="inline-flex items-center gap-1 text-xs bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 rounded-md px-1.5 py-0.5">
      <FileText className="h-3 w-3" />
      {partesCount}
    </span>
  );

  const renderJornadaBadge = () => (
    <span className="inline-flex items-center gap-1 text-xs bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-500/30 rounded-md px-1.5 py-0.5">
      <Clock className="h-3 w-3" />
      {jornadasCount}
    </span>
  );

  if (variant === 'compact') {
    return (
      <div className={cn("flex items-center gap-1", className)}>
        {hasParte && (
          <span className="text-[10px] bg-emerald-500/20 text-emerald-400 rounded px-1 flex items-center gap-0.5">
            <FileText className="h-2.5 w-2.5" />
            {partesCount}
          </span>
        )}
        {hasJornada && (
          <span className="text-[10px] bg-blue-500/20 text-blue-400 rounded px-1 flex items-center gap-0.5">
            <Clock className="h-2.5 w-2.5" />
            {jornadasCount}
          </span>
        )}
        {needsDocumentation && (
          <span className="text-[10px] bg-red-500/20 text-red-400 rounded px-1 flex items-center gap-0.5">
            <AlertCircle className="h-2.5 w-2.5" />
          </span>
        )}
      </div>
    );
  }

  if (variant === 'badges') {
    const content = (
      <div className={cn("flex flex-wrap gap-1", className)}>
        {hasParte && renderParteBadge()}
        {hasJornada && renderJornadaBadge()}
        {needsDocumentation && (
          <span className="inline-flex items-center gap-1 text-xs bg-red-500/20 text-red-600 dark:text-red-400 border border-red-500/30 rounded-md px-1.5 py-0.5">
            <AlertCircle className="h-3 w-3" />
            Pendiente
          </span>
        )}
        {partialDocumentation && (
          <span className="inline-flex items-center gap-1 text-xs bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30 rounded-md px-1.5 py-0.5">
            <AlertCircle className="h-3 w-3" />
            Incompleto
          </span>
        )}
      </div>
    );

    if (isClickable) {
      return (
        <DocumentacionPopover
          partes={partesData}
          jornadas={jornadasData}
          ordenId={ordenId}
        >
          {content}
        </DocumentacionPopover>
      );
    }

    return content;
  }

  // Full variant - for detail views
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div className={cn(
        "flex items-center gap-1.5 text-sm",
        hasParte ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"
      )}>
        {hasParte ? <Check className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
        <span>{hasParte ? `${partesCount} parte${partesCount > 1 ? 's' : ''}` : 'Sin parte'}</span>
      </div>
      <div className={cn(
        "flex items-center gap-1.5 text-sm",
        hasJornada ? "text-blue-600 dark:text-blue-400" : "text-muted-foreground"
      )}>
        {hasJornada ? <Check className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
        <span>{hasJornada ? `${jornadasCount} jornada${jornadasCount > 1 ? 's' : ''}` : 'Sin jornada'}</span>
      </div>
    </div>
  );
};
